import { saveBoard, type Board } from './canvasBoard';

/**
 * Board of a canvas project kept in this tab, outside the page component.
 * Navigating to another screen unmounts the canvas, but a generation that has
 * not reached Supabase or AWS yet still writes here and finishes.
 */
type Live = {
  orgId: string
  projectId: string
  /** Null until this tab publishes a board. An empty placeholder must not replace the saved project. */
  board: Board | null
  /** Video nodes whose render promise is still running in this tab. */
  running: Set<string>
  listeners: Set<(board: Board) => void>
}

const lives = new Map<string, Live>()

function key(orgId: string, projectId: string): string {
  return `${orgId}:${projectId}`
}

function slot(orgId: string, projectId: string): Live {
  const id = key(orgId, projectId)
  let live = lives.get(id)
  if (!live) {
    live = { orgId, projectId, board: null, running: new Set(), listeners: new Set() }
    lives.set(id, live)
  }
  return live
}

/** Latest board written while this tab had the project open. Absent on a cold load. */
export function liveBoard(orgId: string, projectId: string): Board | undefined {
  return lives.get(key(orgId, projectId))?.board ?? undefined
}

export function publishLiveBoard(orgId: string, projectId: string, board: Board) {
  const live = slot(orgId, projectId)
  live.board = board
  saveBoard(orgId, projectId, board)
  live.listeners.forEach((listener) => listener(board))
}

export function subscribeLiveBoard(orgId: string, projectId: string, listener: (board: Board) => void): () => void {
  const live = slot(orgId, projectId)
  live.listeners.add(listener)
  return () => live.listeners.delete(listener)
}

/** True while this tab is still rendering that video, even with no job id yet. */
export function isVideoRunning(orgId: string, projectId: string, videoId: string): boolean {
  return lives.get(key(orgId, projectId))?.running.has(videoId) ?? false
}

export function markVideoRunning(orgId: string, projectId: string, videoId: string, on: boolean) {
  const live = slot(orgId, projectId)
  if (on) live.running.add(videoId)
  else live.running.delete(videoId)
}
